import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormInput,
  FormTextarea,
  Row,
  Col,
  Card,
  CardBody
} from "shards-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimesCircle,
  faCheckCircle,
  faPercent,
  faExternalLinkAlt,
  faExclamationTriangle,
  faEnvelope,
  faPhone,
  faEye,
  faFile,
  faDownload,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faRobot,
  faLightbulb,
  faQuestionCircle
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import TrustScoreContainer from "../../components/DisputeTrustScore/TrustScoreContainer";
import AdjustBalanceModal from "../../components/common/AdjustBalanceModal";

const DisputeQuickView = ({ 
  citation, 
  isOpen, 
  toggle, 
  onAccept, 
  onReject, 
  onAdjust, 
  onViewFull, 
  users, 
  isSuperAdmin, 
  aiRecommendation, 
  trustScore,
  className
}) => {
  const [acceptNote, setAcceptNote] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [evidenceLightboxOpen, setEvidenceLightboxOpen] = useState(false);
  const [currentEvidenceIndex, setCurrentEvidenceIndex] = useState(0);
  const [currentEvidenceUrl, setCurrentEvidenceUrl] = useState('');
  const [currentEvidenceName, setCurrentEvidenceName] = useState('');
  
  useEffect(() => {
    // Reset form states when modal opens/closes
    if (!isOpen) {
      setShowAcceptForm(false);
      setShowRejectForm(false);
      setShowAdjustModal(false);
      setAcceptNote('');
      setRejectNote('');
      setEvidenceLightboxOpen(false);
    }
  }, [isOpen]);
  
  const openEvidenceLightbox = (url, name, index) => {
    setCurrentEvidenceUrl(url);
    setCurrentEvidenceName(name);
    setCurrentEvidenceIndex(index);
    setEvidenceLightboxOpen(true);
  };

  const closeEvidenceLightbox = () => {
    setEvidenceLightboxOpen(false);
  };

  const nextEvidence = (e) => {
    e.stopPropagation();
    if (citation.dispute?.evidence && citation.dispute.evidence.length > 0) {
      const nextIndex = (currentEvidenceIndex + 1) % citation.dispute.evidence.length;
      setCurrentEvidenceIndex(nextIndex);
      setCurrentEvidenceUrl(citation.dispute.evidence[nextIndex].url);
      setCurrentEvidenceName(citation.dispute.evidence[nextIndex].name);
    }
  };

  const prevEvidence = (e) => {
    e.stopPropagation();
    if (citation.dispute?.evidence && citation.dispute.evidence.length > 0) {
      const prevIndex = (currentEvidenceIndex - 1 + citation.dispute.evidence.length) % citation.dispute.evidence.length;
      setCurrentEvidenceIndex(prevIndex);
      setCurrentEvidenceUrl(citation.dispute.evidence[prevIndex].url);
      setCurrentEvidenceName(citation.dispute.evidence[prevIndex].name);
    }
  };

  const isImageFile = (url) => {
    const extension = url.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension);
  };

  const isPdfFile = (url) => {
    const extension = url.split('.').pop().toLowerCase();
    return ['pdf'].includes(extension);
  };
  
  if (!citation || !citation.dispute) return null;
  
  return (
    <>
      <Modal size="lg" open={isOpen} toggle={toggle} className={`dispute-modal ${className || ''}`}>
        <style>
          {`
            .dispute-modal .modal-content {
              border-radius: 6px;
              border: none;
              box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
            }
            
            .dispute-header {
              background-color: #F98A01;
              color: white;
              padding: 16px 20px;
              border-top-left-radius: 6px;
              border-top-right-radius: 6px;
            }
            
            .dispute-form {
              background-color: #f8f9fa;
              border-radius: 4px;
              padding: 15px;
              margin-top: 15px;
            }
            
            .field-label {
              font-size: 11px;
              text-transform: uppercase;
              color: #818ea3;
              margin-bottom: 4px;
              font-weight: 500;
            }
            
            .field-value {
              font-size: 14px;
              color: #495057;
              margin-bottom: 15px;
            }
            
            .info-section {
              margin-bottom: 16px;
            }
            
            .action-button {
              border-radius: 4px;
              font-weight: 500;
              transition: all 0.2s ease;
              padding: 8px 16px;
              font-size: 14px;
            }
            
            .action-button.adjust {
              background-color: #FAD100;
              border-color: #FAD100;
              color: #000;
            }
            
            .action-button.adjust:hover {
              background-color: #e6bf00;
              border-color: #e6bf00;
            }
            
            .action-button.accept {
              background-color: #21AA73;
              border-color: #21AA73;
              color: #fff;
            }
            
            .action-button.accept:hover {
              background-color: #1d9966;
              border-color: #1d9966;
            }
            
            .action-button.reject {
              background-color: #C81712;
              border-color: #C81712;
              color: #fff;
            }
            
            .action-button.reject:hover {
              background-color: #b41510;
              border-color: #b41510;
            }
            
            .evidence-item {
              display: flex;
              align-items: center;
              padding: 8px 12px;
              border-radius: 4px;
              border: 1px solid #eaedf3;
              background-color: #f8f9fa;
              margin-bottom: 8px;
              cursor: pointer;
              transition: all 0.2s ease;
            }
            
            .evidence-item:hover {
              background-color: #e9ecef;
              border-color: #dfe3e9;
            }
            
            .evidence-item .icon {
              color: #21AA73;
              margin-right: 12px;
            }
            
            .evidence-name {
              font-size: 14px;
              color: #495057;
              flex: 1;
            }
            
            .percentage-button {
              cursor: pointer;
              height: 50px;
              width: 60px;
              border-radius: 4px;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s ease;
              border: 1px solid #dfe3e9;
              background-color: #f8f9fa;
              margin-right: 10px;
            }
            
            .percentage-button.active {
              background-color: rgba(33, 170, 115, 0.1);
              border-color: #21AA73;
            }
            
            .percentage-button span {
              font-size: 16px;
              color: #818ea3;
            }
            
            .percentage-button.active span {
              color: #21AA73;
              font-weight: 500;
            }
            
            .adjustment-options {
              background-color: #fff;
              border: 1px solid #eaedf3;
              border-radius: 6px;
              padding: 15px;
              margin-bottom: 15px;
            }
            
            .adjustment-section-title {
              font-size: 13px;
              font-weight: 600;
              color: #495057;
              margin-bottom: 10px;
            }
            
            .adjustment-divider {
              display: flex;
              align-items: center;
              margin: 15px 0;
              color: #818ea3;
            }
            
            .adjustment-divider:before,
            .adjustment-divider:after {
              content: "";
              flex: 1;
              border-bottom: 1px solid #eaedf3;
            }
            
            .adjustment-divider span {
              margin: 0 10px;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            
            .amount-input-container {
              display: flex;
              align-items: center;
            }
            
            .amount-input-container .currency-symbol {
              position: absolute;
              left: 12px;
              color: #495057;
              font-weight: 500;
              font-size: 14px;
              z-index: 2;
            }
            
            .amount-input {
              padding-left: 24px !important;
              font-weight: 500;
              color: #21AA73;
              transition: all 0.2s ease;
            }
            
            .amount-summary-box {
              background-color: rgba(33, 170, 115, 0.05);
              border: 1px solid rgba(33, 170, 115, 0.2);
              border-radius: 4px;
              padding: 10px 15px;
              margin-top: 15px;
            }
            
            .amount-row {
              display: flex;
              justify-content: space-between;
              font-size: 13px;
              color: #495057;
              padding: 3px 0;
            }
            
            .amount-row.total {
              margin-top: 3px;
              padding-top: 6px;
              border-top: 1px dashed rgba(33, 170, 115, 0.2);
              font-weight: 600;
            }
            
            .dispute-name-container {
              display: flex;
              align-items: center;
            }
            
            .email-icon, .phone-icon {
              margin-left: 8px;
              color: #21AA73;
              transition: all 0.2s ease;
              font-size: 14px;
              display: inline-block;
            }
            
            .email-icon:hover, .phone-icon:hover {
              transform: scale(1.2);
            }
            
            .lightbox-overlay {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: rgba(0, 0, 0, 0.85);
              z-index: 2000;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            
            .lightbox-content {
              position: relative;
              max-width: 90%;
              max-height: 90%;
            }
            
            .lightbox-image {
              max-width: 100%;
              max-height: 85vh;
              border-radius: 4px;
              box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
            }
            
            .lightbox-close {
              position: absolute;
              top: -40px;
              right: 0;
              color: white;
              font-size: 20px;
              cursor: pointer;
              background: none;
              border: none;
              width: 40px;
              height: 40px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 50%;
              transition: background-color 0.2s ease;
            }
            
            .lightbox-close:hover {
              background-color: rgba(255, 255, 255, 0.1);
            }
            
            .lightbox-nav {
              position: absolute;
              top: 50%;
              transform: translateY(-50%);
              color: white;
              background: rgba(0, 0, 0, 0.5);
              width: 40px;
              height: 40px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 50%;
              cursor: pointer;
              border: none;
              transition: background-color 0.2s ease;
            }
            
            .lightbox-nav:hover {
              background-color: rgba(0, 0, 0, 0.8);
            }
            
            .lightbox-nav.prev {
              left: -50px;
            }
            
            .lightbox-nav.next {
              right: -50px;
            }
            
            .lightbox-counter {
              position: absolute;
              bottom: -30px;
              left: 0;
              right: 0;
              text-align: center;
              color: white;
              font-size: 14px;
            }
            
            .evidence-embed {
              width: 100%;
              height: 80vh;
              border: none;
              border-radius: 4px;
              box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
            }
            
            .evidence-actions {
              position: absolute;
              bottom: -50px;
              left: 0;
              right: 0;
              display: flex;
              justify-content: center;
              gap: 10px;
            }
            
            .evidence-action-btn {
              background: rgba(255, 255, 255, 0.2);
              color: white;
              border: none;
              border-radius: 4px;
              padding: 8px 16px;
              cursor: pointer;
              display: flex;
              align-items: center;
              font-size: 14px;
              transition: background-color 0.2s ease;
            }
            
            .evidence-action-btn:hover {
              background: rgba(255, 255, 255, 0.3);
            }
            
            .evidence-action-btn .icon {
              margin-right: 8px;
            }
            
            .lightbox-title {
              position: absolute;
              top: -40px;
              left: 0;
              color: white;
              font-size: 14px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 70%;
            }
            
            .tooltip-container {
              position: relative;
            }
            
            .tooltip-container .tooltip-content {
              min-width: 200px;
              max-width: 250px;
              width: auto;
            }
            
            .tooltip-icon {
              cursor: pointer;
              margin-left: 5px;
              color: #818ea3;
              transition: color 0.2s ease;
            }
            
            .tooltip-icon:hover {
              color: #3498db;
            }
            
            .tooltip-content {
              position: absolute;
              top: 100%;
              left: 50%;
              transform: translateX(-50%);
              background-color: #fff;
              border: 1px solid #eaedf3;
              border-radius: 4px;
              padding: 10px;
              width: 200px;
              z-index: 1000;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              opacity: 0;
              visibility: hidden;
              transition: opacity 0.2s, visibility 0.2s;
            }
            
            .tooltip-container:hover .tooltip-content {
              opacity: 1;
              visibility: visible;
            }
            
            .tooltip-title {
              font-size: 14px;
              font-weight: 600;
              color: #495057;
              margin-bottom: 5px;
            }
            
            .tooltip-content p {
              margin: 0;
              font-size: 12px;
              color: #818ea3;
            }
            
            .tooltip-content.right {
              left: auto;
              right: 0;
              transform: translateX(0);
            }
            
            /* Ensure tooltips stay within modal bounds */
            .tooltip-container {
              position: relative;
            }
            
            .tooltip-container .tooltip-content {
              min-width: 200px;
              max-width: 250px;
              width: auto;
            }
            
            /* For tooltips near the right edge, open to the left */
            .tooltip-container.right-edge .tooltip-content,
            .col-md-4 .tooltip-container .tooltip-content {
              left: auto;
              right: 0;
              transform: translateX(0);
            }
            
            /* For tooltips near the left edge, open to the right */
            .tooltip-container.left-edge .tooltip-content {
              left: 0;
              right: auto;
              transform: translateX(0);
            }
          `}
        </style>
        <div className="dispute-header">
          <div className="d-flex align-items-center justify-content-between">
            <h6 className="m-0 d-flex align-items-center" style={{fontWeight: 600, fontSize: '18px', color: 'white'}}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              DISPUTED: {citation.violation_number}
            </h6>
            <div className="d-flex align-items-center">
              <div style={{fontSize: '14px', color: 'white', marginRight: '15px'}}>
                {moment(citation.disputed_on).format('MM/DD/YYYY h:mm a')}
              </div>
              <Button
                theme="white"
                className="close-btn p-0"
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  border: 'none',
                  color: 'white'
                }}
                onClick={toggle}
              >
                <FontAwesomeIcon icon={faTimes} size="sm" />
              </Button>
            </div>
          </div>
        </div>
        <ModalBody className="p-4" style={{ maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' }}>
          <Row>
            <Col md={8}>
              <Row>
                <Col xs={12} md={6}>
                  <div className="info-section">
                    <div className="field-label">Violation</div>
                    <div className="field-value">{citation.violation_number}</div>
                  </div>
        
                  <div className="info-section">
                    <div className="field-label">License Plate</div>
                    <div className="field-value">{citation.license_plate}</div>
                  </div>
        
                  <div className="info-section">
                    <div className="field-label">Date/Time</div>
                    <div className="field-value">{moment(citation.created_at).format('MM/DD/YYYY h:mm a')}</div>
                  </div>
        
                  <div className="info-section">
                    <div className="field-label">Officer</div>
                    <div className="field-value">{users.find(user => user.id === citation.user)?.name || 'Unknown'}</div>
                  </div>
                </Col>
                
                <Col xs={12} md={6}>
                  <div className="info-section">
                    <div className="field-label">Disputer Name</div>
                    <div className="field-value dispute-name-container">
                      <span>{citation.dispute?.name}</span>
                    </div>
                  </div>
                  
                  <div className="info-section">
                    <div className="field-label">Email</div>
                    <div className="field-value dispute-name-container">
                      <span>{citation.dispute?.email}</span>
                      {citation.dispute?.email && (
                        <a 
                          href={`mailto:${citation.dispute?.email}`}
                          className="email-icon"
                          title={`Email ${citation.dispute?.name}`}
                        >
                          <FontAwesomeIcon icon={faEnvelope} size="sm" />
                        </a>
                      )}
                    </div>
                  </div>
                  
                  {citation.dispute?.mobile && (
                    <div className="info-section">
                      <div className="field-label">Mobile</div>
                      <div className="field-value dispute-name-container">
                        <span>{citation.dispute?.mobile}</span>
                        <a 
                          href={`tel:${citation.dispute?.mobile}`}
                          className="phone-icon"
                          title={`Call ${citation.dispute?.name}`}
                        >
                          <FontAwesomeIcon icon={faPhone} size="sm" />
                        </a>
                      </div>
                    </div>
                  )}
                  
                  <div className="info-section">
                    <div className="field-label">Current Balance</div>
                    <div className="field-value" style={{fontSize: 18, fontWeight: 500}}>${parseFloat(citation.total).toFixed(2)}</div>
                  </div>
                </Col>
              </Row>
              
              <div className="info-section mt-2">
                <div className="field-label">Reason for Dispute</div>
                <div className="field-value" style={{backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px', borderLeft: '3px solid #F98A01'}}>
                  {citation.dispute?.reason}
                </div>
              </div>
              
              {citation.dispute?.evidence?.length > 0 && (
                <div className="info-section">
                  <div className="field-label">Evidence</div>
                  <div>
                    {citation.dispute.evidence.map((evidence, index) => (
                      <div 
                        key={index} 
                        className="evidence-item"
                        onClick={() => openEvidenceLightbox(evidence.url, evidence.name, index)}
                      >
                        <FontAwesomeIcon icon={faFile} className="icon" />
                        <span className="evidence-name">{evidence.name}</span>
                        <FontAwesomeIcon icon={faEye} style={{color: '#818ea3'}} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Col>
            
            <Col md={4}>
              {/* Email verification trust score - only for super admins */}
              {isSuperAdmin && (
                <div className="mb-4">
                  <TrustScoreContainer 
                    email={citation.dispute?.email} 
                    name={citation.dispute?.name} 
                  />
                </div>
              )}
              
              {/* AI Recommendation (only for super admins) */}
              {isSuperAdmin && aiRecommendation && (
                <Card className="mb-4">
                  <CardBody className="p-3">
                    <h6 className="mb-2 d-flex align-items-center font-weight-bold" style={{ fontSize: '14px' }}>
                      <FontAwesomeIcon icon={faRobot} className="mr-2" style={{color: '#3498db'}} />
                      AI Recommendation
                    </h6>
                    
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div style={{
                          backgroundColor: aiRecommendation.confidence >= 80 ? 'rgba(33, 170, 115, 0.1)' : 
                            aiRecommendation.confidence >= 60 ? 'rgba(249, 164, 63, 0.1)' : 'rgba(200, 23, 18, 0.1)',
                          color: aiRecommendation.confidence >= 80 ? '#21AA73' : 
                            aiRecommendation.confidence >= 60 ? '#F9A43F' : '#C81712',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {aiRecommendation.action}
                        </div>
                        <div className="d-flex align-items-center">
                          <div style={{
                            backgroundColor: aiRecommendation.confidence >= 80 ? 'rgba(33, 170, 115, 0.1)' : 
                              aiRecommendation.confidence >= 60 ? 'rgba(249, 164, 63, 0.1)' : 'rgba(200, 23, 18, 0.1)',
                            color: aiRecommendation.confidence >= 80 ? '#21AA73' : 
                              aiRecommendation.confidence >= 60 ? '#F9A43F' : '#C81712',
                            padding: '2px 6px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            {aiRecommendation.confidence}%
                          </div>
                          <div className="tooltip-container ml-2">
                            <FontAwesomeIcon icon={faQuestionCircle} className="tooltip-icon" />
                            <div className="tooltip-content" style={{ 
                              left: 'auto', 
                              right: '0', 
                              top: 'calc(100% + 5px)',
                              width: '220px',
                              transform: 'none'
                            }}>
                              <div className="tooltip-title">Recommendation Rationale</div>
                              <p>{aiRecommendation.reason}</p>
                              <p className="mb-0" style={{fontSize: '11px', color: '#818ea3'}}>
                                Based on customer trust score of {trustScore}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      block
                      theme={
                        aiRecommendation.action.toLowerCase().includes('accept') ? 'success' :
                        aiRecommendation.action.toLowerCase().includes('adjust') ? 'info' :
                        aiRecommendation.action.toLowerCase().includes('reject') ? 'danger' : 'success'
                      }
                      size="sm"
                      onClick={() => {
                        // Apply the recommended action based on type
                        if (aiRecommendation.action.toLowerCase().includes('accept')) {
                          onAccept(citation);
                          toggle(); // Close modal after action
                        } else if (aiRecommendation.action.toLowerCase().includes('reject')) {
                          onReject(citation);
                          toggle(); // Close modal after action
                        } else if (aiRecommendation.action.toLowerCase().includes('adjust')) {
                          onAdjust(citation, 50); // Default 50% adjustment
                          toggle(); // Close modal after action
                        }
                      }}
                      style={{
                        boxShadow: 'none',
                        border: aiRecommendation.action.toLowerCase().includes('accept') ? '1px solid #21AA73' :
                               aiRecommendation.action.toLowerCase().includes('adjust') ? '1px solid #3498db' :
                               aiRecommendation.action.toLowerCase().includes('reject') ? '1px solid #C81712' : '1px solid #21AA73',
                        borderRadius: '4px',
                        fontWeight: '500',
                        fontSize: '12px',
                        transition: 'all 0.2s ease',
                        backgroundColor: aiRecommendation.action.toLowerCase().includes('accept') ? '#21AA73' :
                                        aiRecommendation.action.toLowerCase().includes('adjust') ? '#3498db' :
                                        aiRecommendation.action.toLowerCase().includes('reject') ? '#C81712' : '#21AA73',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon 
                        icon={
                          aiRecommendation.action.toLowerCase().includes('accept') ? faCheckCircle :
                          aiRecommendation.action.toLowerCase().includes('adjust') ? faPercent :
                          aiRecommendation.action.toLowerCase().includes('reject') ? faTimesCircle : faRobot
                        } 
                        className="mr-2" 
                      />
                      Apply Recommendation
                    </Button>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>

          {showAcceptForm && (
            <div className="dispute-form">
              <div className="field-label">Accept Note (seen by customer)</div>
              <FormTextarea
                value={acceptNote}
                onChange={(e) => setAcceptNote(e.target.value)}
                style={{marginBottom: '15px', height: '100px'}}
              />
              <div className="d-flex justify-content-end">
                <Button theme="light" onClick={() => setShowAcceptForm(false)} className="mr-2">
                  Cancel
                </Button>
                <Button 
                  theme="accent" 
                  onClick={() => {
                    onAccept(citation, acceptNote);
                    toggle();
                  }}
                >
                  Accept Dispute
                </Button>
              </div>
            </div>
          )}
          
          {showRejectForm && (
            <div className="dispute-form">
              <div className="field-label">Rejection Note (seen by customer)</div>
              <FormTextarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                style={{marginBottom: '15px', height: '100px'}}
              />
              <div className="d-flex justify-content-end">
                <Button theme="light" onClick={() => setShowRejectForm(false)} className="mr-2">
                  Cancel
                </Button>
                <Button 
                  theme="accent" 
                  onClick={() => {
                    onReject(citation, rejectNote);
                    toggle();
                  }}
                >
                  Reject Dispute
                </Button>
              </div>
            </div>
          )}
        </ModalBody>
        
        {!showAcceptForm && !showRejectForm && (
          <ModalFooter>
            <div className="d-flex justify-content-between align-items-center w-100">
              <div>
                <Button
                  theme="secondary"
                  className="action-button adjust mr-2"
                  onClick={() => setShowAdjustModal(true)}
                >
                  <FontAwesomeIcon icon={faPercent} className="mr-1" />
                  Adjust
                </Button>
                <Button
                  theme="secondary"
                  className="action-button reject mr-2"
                  onClick={() => setShowRejectForm(true)}
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                  Reject
                </Button>
                <Button
                  theme="secondary"
                  className="action-button accept mr-2"
                  onClick={() => setShowAcceptForm(true)}
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                  Accept
                </Button>
              </div>
              <Button
                theme="accent"
                onClick={() => {
                  onViewFull(citation);
                  toggle();
                }}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                View Full Citation
              </Button>
            </div>
          </ModalFooter>
        )}
      </Modal>
      
      {/* Add the shared adjust balance modal */}
      <AdjustBalanceModal 
        isOpen={showAdjustModal}
        toggle={() => setShowAdjustModal(false)}
        citation={citation}
        onAdjust={(citation, amount, note) => {
          onAdjust(citation, amount, note);
          toggle();
        }}
      />
      
      {/* Evidence Lightbox */}
      {evidenceLightboxOpen && citation.dispute?.evidence && citation.dispute.evidence.length > 0 && (
        <div className="lightbox-overlay" onClick={closeEvidenceLightbox}>
          <div className="lightbox-content">
            <div className="lightbox-title">{currentEvidenceName}</div>
            <button className="lightbox-close" onClick={closeEvidenceLightbox}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            {isImageFile(currentEvidenceUrl) ? (
              <img 
                src={currentEvidenceUrl} 
                alt="Evidence" 
                className="lightbox-image" 
                onClick={(e) => e.stopPropagation()}
              />
            ) : isPdfFile(currentEvidenceUrl) ? (
              <iframe 
                src={currentEvidenceUrl} 
                className="evidence-embed"
                title="Evidence document"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div style={{
                color: 'white', 
                textAlign: 'center', 
                padding: '20px',
                backgroundColor: 'rgba(0,0,0,0.5)',
                borderRadius: '8px'
              }}>
                <FontAwesomeIcon icon={faFile} size="3x" style={{marginBottom: '15px'}} />
                <p>This file type cannot be previewed in the browser.</p>
              </div>
            )}
            
            {citation.dispute.evidence.length > 1 && (
              <>
                <button className="lightbox-nav prev" onClick={prevEvidence}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button className="lightbox-nav next" onClick={nextEvidence}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
                <div className="lightbox-counter">
                  {currentEvidenceIndex + 1} / {citation.dispute.evidence.length}
                </div>
              </>
            )}
            
            <div className="evidence-actions">
              <a 
                href={currentEvidenceUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="evidence-action-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} className="icon" />
                Open in New Tab
              </a>
              <a 
                href={currentEvidenceUrl} 
                download={currentEvidenceName} 
                className="evidence-action-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <FontAwesomeIcon icon={faDownload} className="icon" />
                Download
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DisputeQuickView; 